import type { System, SystemFactory } from '../core/system';
import { TIPS, type TipFacts } from '../onboarding/tips';

/**
 * Loading state. index.html ships the whole `#loading` screen with inline critical CSS (the painted street,
 * the logo, an empty bar and a tip slot), so the very first frame is already the game's — see docs/UI.md,
 * "Splash and loading". The steps below are tiny systems registered at spaced `order` values: each one
 * advances the progress bar, then yields to the browser so the bar actually paints before the next heavy
 * system initialises. Progress is therefore honest: it moves as real init work completes. Long waits inside a
 * step (the street model pack with its KTX2 textures, the city build) report through `loadingProgress`.
 */
interface Step {
  order: number;
  pct: number;
  label: string;
}

/** The label shows while the systems after `order` (up to the next step) initialise. */
export const STEPS: readonly Step[] = [
  { order: 1, pct: 4, label: 'Unfolding the map' },
  { order: 99, pct: 8, label: 'Paving the streets' }, // world (100): the street pack, then the city build
  { order: 139, pct: 40, label: 'Lighting the lamps' }, // time of day, renderer
  { order: 199, pct: 50, label: 'Putting people on the pavements' }, // player, traffic, peds, police
  { order: 299, pct: 62, label: 'Parking the motors' }, // vehicles, physics
  { order: 499, pct: 76, label: 'Putting the law on the beat' }, // save, economy, heat, missions
  { order: 599, pct: 88, label: 'Tuning the radio' }, // camera, audio, views, UI
  { order: 799, pct: 100, label: 'Opening the pubs' },
];

/** The tips that make sense before you have played a second (onboarding/tips.ts, worded for this device). */
const TIP_IDS = ['car', 'drive', 'book', 'map', 'fence', 'run', 'zoom'];
const TIP_EVERY_MS = 5200;

interface LoadUi {
  bar: HTMLElement;
  fill: HTMLElement;
  label: HTMLElement;
  tip: HTMLElement | null;
}

let current = 0;
let tipTimer: ReturnType<typeof setInterval> | null = null;

function ensureUi(): LoadUi | null {
  const loading = document.getElementById('loading');
  if (!loading) return null;
  let bar = loading.querySelector<HTMLElement>('.bh-load');
  let label = loading.querySelector<HTMLElement>('.bh-load-label');
  if (!bar || !label) {
    bar = document.createElement('div');
    bar.className = 'bh-load';
    bar.appendChild(document.createElement('i'));
    label = document.createElement('div');
    label.className = 'bh-load-label';
    loading.append(label, bar);
  }
  bar.classList.add('live');
  bar.setAttribute('role', 'progressbar');
  bar.setAttribute('aria-label', 'Loading');
  return { bar, fill: bar.firstElementChild as HTMLElement, label, tip: loading.querySelector<HTMLElement>('.ld-tip') };
}

function setBar(ui: LoadUi, pct: number): void {
  current = Math.max(current, Math.min(100, pct));
  ui.fill.style.width = `${current}%`;
  ui.bar.setAttribute('aria-valuenow', String(Math.round(current)));
}

/** The loading tips for this device, in a stable shuffled order (exported for tests). */
export function loadingTips(touch: boolean, seed = 0): string[] {
  const facts = { touch, touchLeftHanded: false, online: false } as unknown as TipFacts;
  const tips = TIP_IDS.map((id) => TIPS.find((t) => t.id === id)?.text(facts)).filter((t): t is string => !!t);
  const start = Math.abs(seed) % Math.max(1, tips.length);
  return [...tips.slice(start), ...tips.slice(0, start)];
}

function startTips(ui: LoadUi): void {
  const tip = ui.tip;
  if (!tip || tipTimer) return;
  const touch = typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches;
  const tips = loadingTips(touch, Math.floor(Date.now() / 1000));
  if (!tips.length) return;
  let i = 0;
  const show = (): void => {
    const b = document.createElement('b');
    b.textContent = 'Tip';
    tip.replaceChildren(b, document.createTextNode(tips[i % tips.length]!));
    i++;
  };
  show();
  tipTimer = setInterval(() => {
    if (!tip.isConnected || tip.closest('.done')) {
      if (tipTimer) clearInterval(tipTimer);
      tipTimer = null;
      return;
    }
    tip.classList.add('out');
    setTimeout(() => {
      show();
      tip.classList.remove('out');
    }, 400);
  }, TIP_EVERY_MS);
}

/**
 * A long wait inside one step: `fraction` (0..1) of the way from this step's mark to the next one, with an
 * optional label of its own (the street pack: "Unloading the lorries", the city build: "Laying the setts").
 */
export function loadingProgress(fraction: number, label?: string): void {
  if (typeof document === 'undefined') return;
  const ui = ensureUi();
  if (!ui) return;
  const i = STEPS.findIndex((s) => s.pct > current);
  const from = i > 0 ? STEPS[i - 1]!.pct : current;
  const to = i >= 0 ? STEPS[i]!.pct : 100;
  setBar(ui, from + (to - from) * Math.max(0, Math.min(1, fraction)));
  if (label) ui.label.textContent = `${label}…`;
}

/** Resolve after the browser has had a chance to paint (with a timer fallback for hidden tabs). */
function paint(): Promise<void> {
  return new Promise((resolve) => {
    let done = false;
    const finish = (): void => {
      if (!done) {
        done = true;
        resolve();
      }
    };
    requestAnimationFrame(() => setTimeout(finish, 0));
    setTimeout(finish, 80);
  });
}

function createStep(step: Step): System {
  return {
    id: `loading-${step.order}`,
    order: step.order,
    async init() {
      if (typeof document === 'undefined') return;
      const ui = ensureUi();
      if (!ui) return;
      if (step.order === STEPS[0]!.order) {
        current = 0;
        startTips(ui);
      }
      setBar(ui, step.pct);
      ui.label.textContent = `${step.label}…`;
      await paint();
    },
  };
}

/** One factory per step, for `SYSTEMS`. */
export function createLoadingSteps(): SystemFactory[] {
  return STEPS.map((s) => () => createStep(s));
}
